import React, { useState } from "react";
import {
  Link,
  Stack,
  Alert,
  IconButton,
  InputAdornment,
  Container,
  Box,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import { useNavigate, useLocation, Link as RouterLink } from "react-router-dom";

import { FCheckbox, FormProvider, FTextField } from "../../components/form";
import useAuth from "../../hooks/useAuth";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";

const LoginSchema = Yup.object().shape({
  email: Yup.string().email("Invalid email").required("Email is required"),
  password: Yup.string().required("Password is required"),
});

const defaultValues = {
  email: "",
  password: "",
  remember: true,
};

function UserLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const [showPassword, setShowPassword] = useState(false);

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });
  const {
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = methods;

  const onSubmit = async (data) => {
    let { email, password } = data;
    try {
      await auth.login({ email, password }, (user) => {
        const from = location.state?.from?.pathname || `/user/${user._id}`;
        if (user.role === "user") {
          navigate(from, { replace: true });
        } else if (user.role === "partner") {
          navigate(`/partner/${user._id}`, { replace: true });
        }
      });
    } catch (error) {
      reset();
      setError("responseError", error);
    }
  };

  return (
    <Box sx={{ background: "#ffb95e", height: "80vh" }}>
      <Container maxWidth="xs" sx={{ background: "#fff", padding: "5rem" }}>
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={3}>
            {!!errors.responseError && (
              <Alert severity="error">
                {errors.responseError.response.data.errors.message}
              </Alert>
            )}
            <Alert severity="info">
              Don’t have an account?{" "}
              <Link
                variant="subtitle2"
                component={RouterLink}
                to="/userRegister"
              >
                Get started
              </Link>
            </Alert>

            <FTextField name="email" label="Email address" />

            <FTextField
              name="password"
              label="Password"
              type={showPassword ? "text" : "password"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? (
                        <VisibilityIcon />
                      ) : (
                        <VisibilityOffIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ my: 2 }}
          >
            <FCheckbox name="remember" label="Remember me" />
            <Link component={RouterLink} variant="subtitle2" to="/userReset">
              Forgot password?
            </Link>
          </Stack>

          <LoadingButton
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            loading={isSubmitting}
            sx={{ background: "#07A4B5" }}
          >
            Login
          </LoadingButton>
        </FormProvider>
      </Container>
    </Box>
  );
}

export default UserLoginPage;
